import React, { useRef, useState, useEffect, useCallback } from 'react'
import {
  Wrapper,
  Content,
  ButtonContainer
} from './ModalForestFirstAccess.styles'
import Slider from 'react-slick'
import { useTranslation } from 'i18n'
import { firstAcessData, settings } from './constants'
import { useJoinGameMutation } from '__gql-generated__/types'
import { hasForestQuery } from 'api/queries/forest'
import { useQuery } from '@apollo/client'
import type { User } from 'api/data.types'
import { Button, Modal } from '@arvoreeducacao/bonsai-ds'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import {
  onboardingControllerFindOrCreateTutorialEndpoint,
  onboardingControllerUpdateTutorialEndpoint
} from 'api/onboarding-custom'
import {
  UpdateTutorialDtoAction,
  TutorialResponseDtoState
} from 'api/generated/schemas'
import { useOnboardingModalState } from 'hooks/use-onboarding-modal-state'

type ModalFirstAccessProps = {
  refetch: VoidFunction
  onShowPlantFirstTree?: () => void
}

const ModalFirstAccess = ({
  refetch,
  onShowPlantFirstTree
}: ModalFirstAccessProps) => {
  const slider = useRef<Slider>(null)
  const [show, setShow] = useState(false)
  const [activeItem, setActiveItem] = useState(0)
  const { t } = useTranslation()
  const { user } = useUserInfo()
  const { registerModalOpen, registerModalClosed } = useOnboardingModalState()
  const onboardingJustCompletedRef = useRef(false)

  const { data: forestData } = useQuery<{ me: User }>(hasForestQuery, {
    fetchPolicy: 'cache-and-network'
  })

  const hasPlantedTrees =
    (forestData?.me?.reader?.forest?.treeListings?.length ?? 0) > 0

  const [screenAchievementCompleted, setScreenAchievementCompleted] = useState<
    boolean | null
  >(null)

  const createScreenAchievementTutorial = useCallback(async () => {
    if (!user?.reader?.id) {
      return
    }

    try {
      const createResponse =
        await onboardingControllerFindOrCreateTutorialEndpoint({
          name: 'screen_achievement'
        })

      if (createResponse.status === 201 && createResponse.data) {
        await onboardingControllerUpdateTutorialEndpoint({
          id: createResponse.data.id,
          action: UpdateTutorialDtoAction.COMPLETE
        })
      }
    } catch {
      return
    }
  }, [user?.reader?.id])

  useEffect(() => {
    const checkScreenAchievementStatus = async () => {
      if (!user?.reader?.id) {
        return
      }

      try {
        const response = await onboardingControllerFindOrCreateTutorialEndpoint(
          {
            name: 'screen_achievement'
          }
        )

        if (response.status === 201 && response.data) {
          const isCompleted =
            response.data.state === TutorialResponseDtoState.completed
          setScreenAchievementCompleted(isCompleted)
        } else {
          setScreenAchievementCompleted(false)
        }
      } catch {
        setScreenAchievementCompleted(false)
      }
    }

    checkScreenAchievementStatus()
  }, [user?.reader?.id])

  useEffect(() => {
    if (screenAchievementCompleted !== null) {
      const shouldShowModal = !screenAchievementCompleted && !hasPlantedTrees
      setShow(prevShow => {
        if (prevShow !== shouldShowModal) {
          return shouldShowModal
        }
        return prevShow
      })
    }
  }, [screenAchievementCompleted, hasPlantedTrees])

  useEffect(() => {
    if (show) {
      registerModalOpen()
      return () => registerModalClosed()
    }
  }, [show, registerModalOpen, registerModalClosed])

  useEffect(() => {
    if (!show && onboardingJustCompletedRef.current && onShowPlantFirstTree) {
      onboardingJustCompletedRef.current = false
      onShowPlantFirstTree()
      refetch()
    }
  }, [show, onShowPlantFirstTree, refetch])
  const [joinGame] = useJoinGameMutation({
    refetchQueries: [{ query: hasForestQuery }]
  })

  const joinGameWithRetry = async (maxRetries = 3): Promise<boolean> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await joinGame()

        if (
          result?.data?.joinGame !== undefined &&
          result?.data?.joinGame !== null
        ) {
          return true
        }
      } catch {
        if (attempt === maxRetries) {
          return false
        }

        await new Promise(resolve => setTimeout(resolve, 1000 * attempt))
      }
    }

    return false
  }

  const gotoNext = async () => {
    if (
      // @ts-expect-error - slick types are incomplete
      slider.current.innerSlider.state.currentSlide ===
      firstAcessData.length - 1
    ) {
      const joinGameSuccess = await joinGameWithRetry()

      if (joinGameSuccess) {
        try {
          await createScreenAchievementTutorial()
          setScreenAchievementCompleted(true)
          onboardingJustCompletedRef.current = true
        } catch (error) {
          console.error('Erro ao criar tutorial screen_achievement:', error)
        }

        setShow(false)
      } else {
        global.alert(t('Oops, aconteceu um erro, tente novamente mais tarde.'))
        refetch()
      }

      return
    }

    setActiveItem(activeItem + 1)
    slider.current.slickNext()
  }

  const handleBack = () => {
    setActiveItem(activeItem - 1)
    slider.current.slickPrev()
  }

  return (
    <Modal
      isOpen={show}
      type="standard"
      size="medium"
      portalContainer={document.getElementById('modal')}
      onClose={() => {}}
    >
      <div className="flex flex-col max-h-[calc(100vh-120px)]">
        {activeItem < firstAcessData.length && (
          <img
            className="mx-0 md:mx-0 w-full tablet:mx-4 object-cover shrink-0"
            style={{ maxHeight: 'clamp(120px, 25vh, 275px)' }}
            src={firstAcessData[activeItem]?.image}
            alt="Imagem ilustrativa da Floresta da Árvore"
          />
        )}
        <Slider
          {...settings}
          ref={slider}
          className="mt-0 flex-1 min-h-0 overflow-y-auto"
        >
          {firstAcessData.map((item, index) => (
            <Wrapper key={index}>
              <Content>
                <span className="text-[24px] font-bold text-gray-900">
                  {t(item.title)}
                </span>
                <span className="text-[16px] text-gray-700 px-small tablet:p-0">
                  {t(item.description)}
                </span>
              </Content>
              <ButtonContainer
                style={{
                  justifyContent: index === 0 ? 'flex-end' : 'space-between'
                }}
              >
                {index > 0 && (
                  <Button
                    onClick={handleBack}
                    theme={{ type: 'secondary', size: 'large' }}
                    text={t('Voltar')}
                  />
                )}
                <Button
                  onClick={gotoNext}
                  theme={{ type: 'primaryLivros', size: 'large' }}
                  text={
                    index === firstAcessData.length - 1
                      ? t('Finalizar')
                      : t('Avançar')
                  }
                />
              </ButtonContainer>
            </Wrapper>
          ))}
        </Slider>
      </div>
    </Modal>
  )
}

export default ModalFirstAccess
